import { useEffect, type RefObject } from 'react';

/**
 * Keeps a CSS var `--keyboard-inset` in sync with the soft keyboard height.
 *
 * Updates the DOM directly (no React state) so a keyboard open does not
 * re-render heavy conversation trees. Prefer Capactior `keyboardWillShow`
 * when available; fall back to `visualViewport`.
 */
export function useKeyboardBottomInset(
  targetRef?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    let cancelled = false;
    let removeNative: (() => void) | undefined;

    const apply = (px: number) => {
      if (cancelled) return;
      const value = `${Math.max(0, Math.round(px))}px`;
      const el = targetRef?.current;
      if (el) el.style.setProperty('--keyboard-inset', value);
      document.documentElement.style.setProperty('--keyboard-inset', value);
    };

    const syncFromVisualViewport = () => {
      const vv = window.visualViewport;
      if (!vv) return;
      // Layout viewport bottom covered by the keyboard (and browser chrome).
      const covered = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      apply(covered);
    };

    const vv = window.visualViewport;
    vv?.addEventListener('resize', syncFromVisualViewport);
    vv?.addEventListener('scroll', syncFromVisualViewport);
    window.addEventListener('resize', syncFromVisualViewport);
    syncFromVisualViewport();

    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { Keyboard } = await import('@capacitor/keyboard');
        const show = await Keyboard.addListener('keyboardWillShow', (info) => {
          apply(info.keyboardHeight);
        });
        const hide = await Keyboard.addListener('keyboardWillHide', () => {
          apply(0);
        });
        removeNative = () => {
          void show.remove();
          void hide.remove();
        };
      } catch {
        // Plugin missing / web — visualViewport path is enough.
      }
    })();

    return () => {
      cancelled = true;
      vv?.removeEventListener('resize', syncFromVisualViewport);
      vv?.removeEventListener('scroll', syncFromVisualViewport);
      window.removeEventListener('resize', syncFromVisualViewport);
      removeNative?.();
      document.documentElement.style.setProperty('--keyboard-inset', '0px');
      const el = targetRef?.current;
      if (el) el.style.setProperty('--keyboard-inset', '0px');
    };
  }, [targetRef]);
}
